import type { VenueProfileFieldError } from '@/lib/api/errors'

import { RESERVATION_UNIT_OPTIONS } from './courtTypes'
import type {
  ManagedVenueItem,
  ManagedVenueListView,
  VenueOperationVerification,
} from './managerVenueTypes'
import type {
  VenueFacilityType,
  VenueRegisterCourtInput,
  VenueRegisterResult,
  VenueRegisterSaveInput,
} from './venueRegisterTypes'

/**
 * 등록 폼 상태 ↔ `VenueRegisterSaveInput` 매핑 + 전량 수집 클라이언트 검증 + 등록 직후 캐시 병합
 * (design.md §2). `venueProfileViewModel.ts`·`courtViewModel.ts` 와 같은 컨벤션을 따른다.
 *
 * 🚨 `if (status === ...)` 를 페이지에 흩지 않는다 — 이 파일이 규칙의 단일 출처다.
 */

/** 텍스트 입력은 항상 문자열로 다룬다(빈 문자열이 유효한 중간 상태) — 저장 직전에만 `null` 로 정규화한다. */
export interface VenueRegisterCourtFormState {
  name: string
  operatingStartTime: string
  operatingEndTime: string
  /** 고정 4종 select — 항상 유효한 값 하나를 갖는다. */
  reservationUnitMinutes: number
  maxReservationMinutes: string
}

export interface VenueRegisterFormState {
  name: string
  facilityType: VenueFacilityType
  address: string
  detailAddress: string
  contact: string
  locationGuide: string
  usageGuide: string
  amenities: string[]
  rentalEquipments: string[]
  /** `#865` 로 3상태 폐기 — 항상 `boolean`(기본값 `false`). */
  parkingAvailable: boolean
  showerAvailable: boolean
  lockerRoomAvailable: boolean
  /** "코트 정보(선택)" 섹션 — 0개면 구장만 생성된다(설계 §2 자율 결정, 코트 0개 허용). */
  courts: VenueRegisterCourtFormState[]
}

const DEFAULT_COURT_RESERVATION_UNIT_MINUTES = RESERVATION_UNIT_OPTIONS[1]?.value ?? 60
const DEFAULT_COURT_START_TIME = '06:00'
const DEFAULT_COURT_END_TIME = '22:00'

export function emptyVenueRegisterCourtFormState(): VenueRegisterCourtFormState {
  return {
    name: '',
    operatingStartTime: DEFAULT_COURT_START_TIME,
    operatingEndTime: DEFAULT_COURT_END_TIME,
    reservationUnitMinutes: DEFAULT_COURT_RESERVATION_UNIT_MINUTES,
    maxReservationMinutes: '',
  }
}

export function emptyVenueRegisterFormState(): VenueRegisterFormState {
  return {
    name: '',
    facilityType: 'BADMINTON',
    address: '',
    detailAddress: '',
    contact: '',
    locationGuide: '',
    usageGuide: '',
    amenities: [],
    rentalEquipments: [],
    parkingAvailable: false,
    showerAvailable: false,
    lockerRoomAvailable: false,
    courts: [],
  }
}

/** 빈 문자열/공백뿐인 문자열은 미입력(`null`)으로 정규화한다(`venueProfileViewModel.ts` 와 동일 규칙). */
function toNullableText(value: string): string | null {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** 폼 → `POST` 요청 바디. 검증을 통과한 뒤에만 호출한다. */
export function toRegisterInput(form: VenueRegisterFormState): VenueRegisterSaveInput {
  const courts: VenueRegisterCourtInput[] = form.courts.map((court) => ({
    name: court.name.trim(),
    operatingStartTime: court.operatingStartTime,
    operatingEndTime: court.operatingEndTime,
    reservationUnitMinutes: court.reservationUnitMinutes,
    maxReservationMinutes: Number(court.maxReservationMinutes),
  }))

  return {
    name: form.name.trim(),
    facilityType: form.facilityType,
    address: form.address.trim(),
    detailAddress: toNullableText(form.detailAddress),
    contact: toNullableText(form.contact),
    locationGuide: toNullableText(form.locationGuide),
    usageGuide: toNullableText(form.usageGuide),
    // 🚨 이 화면엔 입력 UI 가 없다 — 항상 null 고정 전송(설계 §2 자율 결정).
    additionalNotice: null,
    amenities: form.amenities,
    rentalEquipments: form.rentalEquipments
      .map((item) => item.trim())
      .filter((item) => item.length > 0),
    parkingAvailable: form.parkingAvailable,
    showerAvailable: form.showerAvailable,
    lockerRoomAvailable: form.lockerRoomAvailable,
    // 🚨 0개면 키 자체를 뺀다 — 서버가 "구장만 생성"으로 해석한다(api-spec §1).
    ...(courts.length > 0 ? { courts } : {}),
  }
}

export const VENUE_REGISTER_LIMITS = {
  name: 100,
  address: 200,
  detailAddress: 200,
  contact: 30,
  locationGuide: 1000,
  usageGuide: 2000,
  amenitiesMax: 12,
  rentalEquipmentsMax: 10,
  rentalEquipmentItemMax: 30,
  courtNameMax: 50,
} as const

const CONTACT_PATTERN = /^(0\d{1,2}-?\d{3,4}-?\d{4}|1\d{3}-?\d{4})$/

const FIELD_LABELS: Record<string, string> = {
  name: '구장 이름',
  address: '주소',
  detailAddress: '상세 주소',
  contact: '연락처',
  locationGuide: '위치 안내',
  usageGuide: '이용 안내',
}

function timeToMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number)
  return (hours ?? 0) * 60 + (minutes ?? 0)
}

/** 최대 예약 시간 검증 — `courtViewModel.ts` 의 같은 규칙을 코드까지 맞춰 미러링한다(api-spec 코드 카탈로그). */
function validateMaxReservationMinutes(
  value: string,
  unitMinutes: number
): { code: string; message: string } | undefined {
  const trimmed = value.trim()
  if (trimmed.length === 0) return { code: 'REQUIRED', message: '최대 예약 시간을 입력해 주세요.' }
  if (!/^\d+$/.test(trimmed)) {
    return { code: 'INVALID_FORMAT', message: '최대 예약 시간은 분 단위 정수로 입력해 주세요.' }
  }
  const minutes = Number(trimmed)
  if (minutes < unitMinutes) {
    return {
      code: 'LESS_THAN_UNIT',
      message: `최대 예약 시간은 예약 단위(${unitMinutes}분) 이상이어야 합니다.`,
    }
  }
  if (minutes % unitMinutes !== 0) {
    return {
      code: 'NOT_MULTIPLE_OF_UNIT',
      message: `최대 예약 시간은 예약 단위(${unitMinutes}분)의 배수여야 합니다. (최대=${minutes}분, 단위=${unitMinutes}분)`,
    }
  }
  return undefined
}

/**
 * 클라이언트 사전 검증. 서버가 최종 판정자이고, 이 표는 api-spec §1 검증표를 미러링한다.
 * 🚨 모든 위반을 한 번에 수집한다(fail-fast 아님 — 서버와 같은 UX). 코트는 `courts[i].xxx` 로
 * 인덱스를 포함해 서버 `fieldErrors[].field` 계약과 글자까지 맞춘다.
 * 🚨 코트 0개는 오류가 아니다(설계 §2 자율 결정 — Figma·api-spec 이 "코트는 나중에"를 명시).
 */
export function validateVenueRegisterForm(form: VenueRegisterFormState): VenueProfileFieldError[] {
  const errors: VenueProfileFieldError[] = []

  function checkText(
    field: keyof typeof VENUE_REGISTER_LIMITS,
    value: string,
    required: boolean,
    pattern?: { regex: RegExp; message: string }
  ) {
    const trimmed = value.trim()
    if (required && trimmed.length === 0) {
      errors.push({
        field,
        code: 'REQUIRED',
        message: `${FIELD_LABELS[field]}을(를) 입력해 주세요.`,
      })
      return
    }
    if (trimmed.length === 0) return
    const max = VENUE_REGISTER_LIMITS[field] as number
    if (trimmed.length > max) {
      errors.push({
        field,
        code: 'TOO_LONG',
        message: `${FIELD_LABELS[field]}은(는) 최대 ${max}자까지 입력할 수 있습니다.`,
      })
      return
    }
    if (pattern && !pattern.regex.test(trimmed)) {
      errors.push({ field, code: 'INVALID_FORMAT', message: pattern.message })
    }
  }

  checkText('name', form.name, true)
  checkText('address', form.address, true)
  checkText('detailAddress', form.detailAddress, false)
  checkText('contact', form.contact, false, {
    regex: CONTACT_PATTERN,
    message: '연락처 형식이 올바르지 않습니다. (예: 02-000-0000)',
  })
  checkText('locationGuide', form.locationGuide, false)
  checkText('usageGuide', form.usageGuide, false)

  if (form.amenities.length > VENUE_REGISTER_LIMITS.amenitiesMax) {
    errors.push({
      field: 'amenities',
      code: 'TOO_MANY_ITEMS',
      message: `편의시설은 최대 ${VENUE_REGISTER_LIMITS.amenitiesMax}개까지 선택할 수 있습니다.`,
    })
  }

  if (form.rentalEquipments.length > VENUE_REGISTER_LIMITS.rentalEquipmentsMax) {
    errors.push({
      field: 'rentalEquipments',
      code: 'TOO_MANY_ITEMS',
      message: `대여 장비는 최대 ${VENUE_REGISTER_LIMITS.rentalEquipmentsMax}개까지 등록할 수 있습니다.`,
    })
  } else if (form.rentalEquipments.some((item) => item.trim().length === 0)) {
    errors.push({
      field: 'rentalEquipments',
      code: 'BLANK_ITEM',
      message: '빈 대여 장비 항목이 있습니다.',
    })
  } else if (
    form.rentalEquipments.some(
      (item) => item.trim().length > VENUE_REGISTER_LIMITS.rentalEquipmentItemMax
    )
  ) {
    errors.push({
      field: 'rentalEquipments',
      code: 'TOO_LONG',
      message: `대여 장비 이름은 항목당 최대 ${VENUE_REGISTER_LIMITS.rentalEquipmentItemMax}자까지 입력할 수 있습니다.`,
    })
  }

  const seenCourtNames = new Set<string>()
  form.courts.forEach((court, index) => {
    const prefix = `courts[${index}]`
    const trimmedName = court.name.trim()

    if (trimmedName.length === 0) {
      errors.push({
        field: `${prefix}.name`,
        code: 'REQUIRED',
        message: '코트 이름을 입력해 주세요.',
      })
    } else if (trimmedName.length > VENUE_REGISTER_LIMITS.courtNameMax) {
      errors.push({
        field: `${prefix}.name`,
        code: 'TOO_LONG',
        message: `코트 이름은 최대 ${VENUE_REGISTER_LIMITS.courtNameMax}자까지 입력할 수 있습니다.`,
      })
    } else {
      // 대소문자·공백 무시 중복 판정(api-spec §1 과 동일 규칙).
      const key = trimmedName.toLowerCase().replace(/\s+/g, '')
      if (seenCourtNames.has(key)) {
        errors.push({
          field: `${prefix}.name`,
          code: 'DUPLICATED',
          message: '같은 이름의 코트가 이미 있습니다.',
        })
      } else {
        seenCourtNames.add(key)
      }
    }

    if (timeToMinutes(court.operatingEndTime) <= timeToMinutes(court.operatingStartTime)) {
      errors.push({
        field: `${prefix}.operatingEndTime`,
        code: 'END_BEFORE_START',
        message: `운영 종료 시각은 시작 시각보다 뒤여야 합니다. (시작=${court.operatingStartTime}, 종료=${court.operatingEndTime})`,
      })
    }

    const maxError = validateMaxReservationMinutes(
      court.maxReservationMinutes,
      court.reservationUnitMinutes
    )
    if (maxError) {
      errors.push({
        field: `${prefix}.maxReservationMinutes`,
        code: maxError.code,
        message: maxError.message,
      })
    }
  })

  return errors
}

/**
 * 등록 직후 캐시 병합(design.md §2 D1) — 관리 구장 목록 쿼리를 재조회하지 않고 새 구장을 직접
 * 합성해 넣는다. Reader 복제 지연 때문에 재조회가 방금 만든 구장을 놓치면 `VenueLayout` 이
 * "접근 불가"로 튕긴다.
 *
 * 🚨 응답에 없는 표시용 필드(`address`·`facilityType` 등)는 **제출한 입력값**에서 가져온다 —
 * 서버가 그대로 저장했을 값이라 신뢰할 수 있다. `stateId`/`cityId` 는 이 화면이 수집하지 않아 빈
 * 문자열로 채운다(목록 화면이 그 값을 렌더에 쓰지 않는다 — `VenueSelectPage.tsx`/`VenueCard` 실측).
 */
export function mergeRegisteredVenue(
  current: ManagedVenueListView | undefined,
  result: VenueRegisterResult,
  submitted: VenueRegisterSaveInput
): ManagedVenueListView {
  const item: ManagedVenueItem = {
    venueId: result.venueId,
    name: result.name,
    venueType: 'HOSTED',
    facilityType: submitted.facilityType,
    reviewStatus: 'APPROVED',
    exposureStatus: 'EXPOSED',
    userVisible: result.userVisible,
    // 🚨 등록 직후는 항상 미인증(§ result.operationVerificationStatus 주석) — verifiedUntil·remainingDays 는
    // 값이 생기기 전이라 null 이다.
    operationVerification: {
      status: result.operationVerificationStatus as VenueOperationVerification['status'],
      statusLabel: result.operationVerificationStatusLabel,
      verifiedUntil: null,
      remainingDays: null,
    },
    reservationEnabled: result.reservationEnabled,
    courtCount: submitted.courts?.length ?? 0,
    stateId: '',
    cityId: '',
    address: submitted.address,
  }

  if (!current) {
    return {
      baseTime: result.baseTime,
      expiringWithinDays: 7,
      venues: [item],
    }
  }

  return {
    ...current,
    venues: [...current.venues, item].sort((a, b) =>
      a.venueId.localeCompare(b.venueId, undefined, { numeric: true })
    ),
  }
}
